/**
 * Theme Context
 *
 * Manages light/dark theme preference for the web portal.
 * Persists the user's choice in localStorage and toggles the `.light`
 * class on `<html>`.
 *
 * IMPORTANT: The CSS token system (see `src/index.css`) defines dark tokens
 * on `:root` and light tokens on the `.light` class. To activate light mode
 * we ADD `.light` to `<html>`; to activate dark mode we REMOVE it.
 * (There is a legacy `.dark` class aliased to :root for compat, but it is
 * NOT used as a mode switch here — presence/absence of `.light` is the
 * sole source of truth.)
 *
 * App-wide default is LIGHT. Dark mode is opt-in via the toggle.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";

export type ThemeMode = "light" | "dark";

interface ThemeContextValue {
  /** Current resolved theme */
  theme: ThemeMode;
  /** Whether the resolved appearance is dark */
  isDark: boolean;
  /** Set a specific theme mode */
  setTheme: (mode: ThemeMode) => void;
  /** Toggle between light and dark */
  toggleTheme: () => void;
}

const STORAGE_KEY = "trainerpulse-theme";
const DEFAULT_THEME: ThemeMode = "light";

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/**
 * Apply or remove the `light` class on `<html>`.
 * Dark is the :root default, so light mode = add `.light`.
 */
function applyThemeClass(theme: ThemeMode): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (theme === "light") {
    root.classList.add("light");
  } else {
    root.classList.remove("light");
  }
}

/** Read persisted preference; fall back to DEFAULT_THEME (light). */
function readStoredTheme(): ThemeMode {
  if (typeof window === "undefined") return DEFAULT_THEME;
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === "light" || saved === "dark") return saved;
  } catch {
    // localStorage unavailable (e.g. private browsing)
  }
  return DEFAULT_THEME;
}

/**
 * ThemeProvider wraps the app and manages theme state.
 *
 * Place this high in the provider tree (inside QueryClientProvider)
 * so all child components can consume the theme via `useTheme()`.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeMode>(() => readStoredTheme());

  const setTheme = useCallback((mode: ThemeMode) => {
    setThemeState(mode);
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Ignore storage errors
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((current) => {
      const next: ThemeMode = current === "light" ? "dark" : "light";
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Ignore storage errors
      }
      return next;
    });
  }, []);

  // Apply the class whenever theme changes. Runs on mount too, so the
  // initial render syncs <html> with the persisted/default preference.
  useEffect(() => {
    applyThemeClass(theme);
  }, [theme]);

  const isDark = theme === "dark";

  return (
    <ThemeContext.Provider value={{ theme, isDark, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook to access the current theme and toggle functions.
 *
 * Must be called within a `<ThemeProvider>`.
 */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return ctx;
}
